import type { I18n } from '@lingui/core';
import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import type { FieldValues } from 'react-hook-form';
import type { z } from 'zod';

import { resolvePatternText, type PatternText } from '@/shared/patterns';
import type { ActionPreview } from '@/shared/screen';
import type { DialogSize } from '@/shared/ui/dialog';

/** Só ação com efeito passa pelo diálogo; `read`/`navigate`/`draft` rodam na hora (U39 §6). */
export type EffectKind = 'write' | 'destructive';

export interface ActionSuccess {
  readonly title: PatternText;
  readonly description?: PatternText;
}

/**
 * Uma ação com efeito como a tela a executa (P10, 06 §3.e): o que o diálogo diz, o formulário curto
 * (opcional), o envio e o retorno. `C` é o contexto de **um** registro; em lote, o diálogo roda o
 * `submit` uma vez por registro (§11.1 do 02: N requisições).
 */
export interface ActionDefinition<C, V extends FieldValues = Record<string, never>, R = unknown> {
  /** Mesmo id da ação no manifesto (`creditNote.rejectCredit`), para ⌘K, agente e telemetria. */
  readonly id: string;
  readonly kind: EffectKind;
  /** Título do diálogo ("Rejeitar operação"). */
  readonly title: (ctx: C) => PatternText;
  /** O que vai acontecer, em uma frase, com o nome do registro (U39 §6): é a prévia legível. */
  readonly summary: (ctx: C) => PatternText;
  /** Nome curto do registro ("#48213", "FIDC Aurora"): vai no toast e na lista de falhas do lote. */
  readonly record: (ctx: C) => string;
  /** Verbo do botão de confirmação, em minúsculas: "rejeitar" vira "Sim, rejeitar" (U20-6). */
  readonly verb: PatternText;
  readonly icon?: LucideIcon;
  /** Validação do formulário. Recebe o `i18n` para as mensagens saírem no idioma ativo (U33). */
  readonly schema?: (ctx: C, i18n: I18n) => z.ZodType<V, V>;
  readonly defaults?: (ctx: C) => V;
  /** Campos do formulário, dentro do `FormProvider` do diálogo. Ausente = só confirmar (P09). */
  readonly Fields?: ComponentType<{ readonly ctx: C }>;
  /** Motivo que trava o confirmar (aviso no topo, botão desabilitado), como o cancelar em Garantia. */
  readonly blocked?: (ctx: C) => PatternText | undefined;
  /**
   * O efeito. Chame a mutação do módulo **sem** feedback próprio (`success: false, error: false`):
   * o toast e o erro saem daqui, da tela (X06).
   */
  readonly submit: (values: V, ctx: C) => Promise<R>;
  /** Toast de sucesso. Ausente = "Feito: <título>"; `false` = sem toast. */
  readonly success?: ((result: R, values: V, ctx: C) => ActionSuccess) | false;
  /** Estorno, quando a API permite: vira **Desfazer** no toast por 5 s (U39 §6). */
  readonly undo?: (result: R, values: V, ctx: C) => Promise<unknown>;
  /** iPhone: bottom sheet (padrão) ou tela cheia, para formulário longo como o rejeitar. */
  readonly mobile?: 'sheet' | 'screen';
  /** Largura do diálogo na web. Ausente = automática (`actionDialogSize`). */
  readonly size?: DialogSize;
  /** Frase do lote ("3 operações em Aprovação de Crédito serão rejeitadas"). Padrão: a do primeiro. */
  readonly batchSummary?: (items: readonly C[]) => PatternText;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- o hospedeiro guarda ações de contextos e valores diferentes
export type AnyActionDefinition = ActionDefinition<any, any, any>;

const ID = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/;

export function validateAction(action: AnyActionDefinition): string[] {
  const issues: string[] = [];
  if (!ID.test(action.id)) issues.push(`ação ${action.id}: id inválido (use domínio.nome, como no manifesto)`);
  const kind: string = action.kind;
  if (kind !== 'write' && kind !== 'destructive') issues.push(`ação ${action.id}: só write ou destructive passam pelo diálogo`);
  if ((action.Fields === undefined) !== (action.schema === undefined)) issues.push(`ação ${action.id}: Fields e schema andam juntos`);
  if (action.Fields && !action.defaults) issues.push(`ação ${action.id}: formulário sem defaults`);
  const verb = typeof action.verb === 'string' ? action.verb : (action.verb.message ?? action.verb.id);
  if (verb.trim() === '') issues.push(`ação ${action.id}: verbo vazio (U20-6)`);
  return issues;
}

/** Declara uma ação com efeito para o `ActionDialog`. Em dev, definição inválida lança erro. */
export function defineAction<C, V extends FieldValues = Record<string, never>, R = unknown>(action: ActionDefinition<C, V, R>): ActionDefinition<C, V, R> {
  if (import.meta.env.DEV) {
    const issues = validateAction(action);
    if (issues.length > 0) throw new Error(issues.join('; '));
  }
  return action;
}

/** A partir de quantos campos o diálogo vira `form` (UUX §14). */
const FORM_DIALOG_MIN_FIELDS = 3;

/**
 * Largura do diálogo da ação (UUX §14): `size` da ação, se houver; senão `form` com 3 ou mais campos
 * nos `defaults` (sem os `*Display`) e `confirm` com menos, sem campos ou só com step-up.
 */
export function actionDialogSize<C, V extends FieldValues, R>(action: ActionDefinition<C, V, R>, ctx: C): DialogSize {
  if (action.size) return action.size;
  if (!action.Fields || !action.defaults) return 'confirm';
  const names = Object.keys(action.defaults(ctx)).filter((name) => !/Display$/.test(name));
  return names.length >= FORM_DIALOG_MIN_FIELDS ? 'form' : 'confirm';
}

/**
 * A mesma prévia no formato do manifesto (U24): use no `preview` da ação para o agente e o ⌘K
 * mostrarem o mesmo texto que o diálogo.
 */
export function actionPreview<C, V extends FieldValues, R>(action: ActionDefinition<C, V, R>, ctx: C, i18n: I18n): ActionPreview {
  return {
    title: resolvePatternText(i18n, action.title(ctx)),
    detail: resolvePatternText(i18n, action.summary(ctx)),
    tone: action.kind === 'destructive' ? 'destructive' : 'default',
  };
}
